import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Container, Typography, Card, CardContent, Button, Stack,
  Chip, Divider, CircularProgress, Alert
} from '@mui/material';
import {
  CheckCircle, Telegram, OpenInNew, Security, CalendarToday,
  NotificationsActive, ArrowForward
} from '@mui/icons-material';
import PublicNavbar from '../components/common/PublicNavbar';
import PublicFooter from '../components/common/PublicFooter';
import { telegramSubscription } from '../services/api';
import useStore from '../store/useStore';

export default function PaymentSuccessPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = useStore((s) => s.token);

  const [loading, setLoading] = useState(false);
  const [subData, setSubData] = useState(location.state || null);

  useEffect(() => {
    // If state wasn't passed via router navigation (e.g. page refresh), fetch from API
    if (!subData && token) {
      setLoading(true);
      telegramSubscription
        .myStatus()
        .then((res) => {
          if (res.data?.success) {
            setSubData({
              telegramRedirectUrl: res.data.data.telegramRedirectUrl,
              subscription: {
                status: res.data.data.subscriptionStatus,
                endDate: res.data.data.endDate,
              },
              telegramConnected: res.data.data.telegramConnected,
              telegramUsername: res.data.data.telegramUsername,
            });
          }
        })
        .catch((err) => {
          console.error('Failed to load status:', err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [subData, token]);

  const telegramUrl =
    subData?.telegramRedirectUrl ||
    'https://t.me/JigendraTradingAlerts_bot';

  const endDateFormatted = subData?.subscription?.endDate
    ? new Date(subData.subscription.endDate).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '30 Days From Today';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0b0f19', color: '#f8fafc' }}>
      <PublicNavbar />

      <Box sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="sm">
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#3b82f6' }} />
              <Typography variant="body1" sx={{ color: '#94a3b8', mt: 2 }}>
                Confirming your subscription...
              </Typography>
            </Box>
          ) : (
            <Card
              sx={{
                bgcolor: '#111827',
                border: '1px solid rgba(16, 185, 129, 0.4)',
                borderRadius: 4,
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 0 40px rgba(16, 185, 129, 0.15)',
                overflow: 'hidden',
                textAlign: 'center',
              }}
            >
              {/* Top Banner */}
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                  py: 4,
                  px: 3,
                }}
              >
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                  }}
                >
                  <CheckCircle sx={{ fontSize: 48, color: '#ffffff' }} />
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 900, color: '#ffffff', mb: 0.5 }}>
                  Payment Successful!
                </Typography>
                <Typography variant="body2" sx={{ color: '#d1fae5', fontWeight: 500 }}>
                  ₹1,000 / Month Telegram Subscription Activated
                </Typography>
              </Box>

              <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                {/* Subscription Details Chip / Box */}
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    bgcolor: 'rgba(30, 41, 59, 0.6)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    mb: 4,
                    textAlign: 'left',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                      Subscription Plan
                    </Typography>
                    <Chip label="Telegram VIP Alerts" size="small" sx={{ bgcolor: 'rgba(59, 130, 246, 0.2)', color: '#60a5fa', fontWeight: 700 }} />
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                      Amount Paid
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: '#ffffff', fontWeight: 700 }}>
                      ₹1,000.00
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                      Status
                    </Typography>
                    <Chip label="ACTIVE" size="small" sx={{ bgcolor: 'rgba(16, 185, 129, 0.2)', color: '#34d399', fontWeight: 700 }} />
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                      Access Valid Until
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: '#fbbf24', fontWeight: 700 }}>
                      {endDateFormatted}
                    </Typography>
                  </Stack>
                </Box>

                {/* THE BIG START TELEGRAM BUTTON */}
                <Box sx={{ mb: 4 }}>
                  <Button
                    component="a"
                    href={telegramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    size="large"
                    fullWidth
                    startIcon={<Telegram sx={{ fontSize: '28px !important' }} />}
                    endIcon={<OpenInNew />}
                    sx={{
                      background: 'linear-gradient(135deg, #0088cc 0%, #006699 100%)',
                      color: '#ffffff',
                      fontWeight: 900,
                      fontSize: { xs: '1.1rem', sm: '1.25rem' },
                      py: 2,
                      borderRadius: 3,
                      boxShadow: '0 10px 30px rgba(0, 136, 204, 0.5)',
                      animation: 'pulse 2s infinite',
                      '@keyframes pulse': {
                        '0%': { transform: 'scale(1)', boxShadow: '0 10px 30px rgba(0, 136, 204, 0.5)' },
                        '50%': { transform: 'scale(1.02)', boxShadow: '0 15px 40px rgba(0, 136, 204, 0.8)' },
                        '100%': { transform: 'scale(1)', boxShadow: '0 10px 30px rgba(0, 136, 204, 0.5)' },
                      },
                      '&:hover': {
                        background: 'linear-gradient(135deg, #0099e6 0%, #0077b3 100%)',
                      },
                    }}
                  >
                    START TELEGRAM
                  </Button>
                  <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 1.5 }}>
                    Click above to open the Telegram bot and activate your live alerts
                  </Typography>
                </Box>

                {/* 3 Steps instructions */}
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    bgcolor: 'rgba(15, 23, 42, 0.5)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    textAlign: 'left',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ color: '#ffffff', fontWeight: 700, mb: 1.5 }}>
                    Quick 3-Step Setup:
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: '#2563eb', color: '#fff', fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 }}>
                        1
                      </Box>
                      <Typography variant="body2" sx={{ color: '#cbd5e1', fontSize: '0.85rem' }}>
                        Click the <strong>[ START TELEGRAM ]</strong> button above to open <strong>@JigendraTradingAlerts_bot</strong>.
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: '#2563eb', color: '#fff', fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 }}>
                        2
                      </Box>
                      <Typography variant="body2" sx={{ color: '#cbd5e1', fontSize: '0.85rem' }}>
                        In Telegram, tap the <strong>START</strong> button at the bottom of the screen.
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: '#10b981', color: '#fff', fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 }}>
                        3
                      </Box>
                      <Typography variant="body2" sx={{ color: '#cbd5e1', fontSize: '0.85rem' }}>
                        The bot will instantly confirm your link and begin broadcasting live notifications directly into your chat!
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
                  <Button
                    component={RouterLink}
                    to="/my-subscription"
                    variant="outlined"
                    fullWidth
                    endIcon={<ArrowForward />}
                    sx={{
                      borderColor: 'rgba(56, 189, 248, 0.4)',
                      color: '#38bdf8',
                      fontWeight: 700,
                      py: 1.2,
                      borderRadius: 2.5,
                      '&:hover': {
                        borderColor: '#38bdf8',
                        bgcolor: 'rgba(56, 189, 248, 0.08)',
                      },
                    }}
                  >
                    Go to My Subscription Portal
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}
        </Container>
      </Box>

      <PublicFooter />
    </Box>
  );
}
